// The twelve countries under the account cards (owner, 2026-10-23): which SES
// account each sends from, who is mailable there now, where it stands, and one
// switch — "Klaviyo is küld" (parallel). Nothing else: N, the waves, the
// cut-over and the log are in "Részletek". The switch asks why, like every
// handover move, and the account's split follows it at once (2.2).

import { useState } from "react";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { DataTable } from "@/components/emc/DataTable";
import { Country } from "@/components/emc/Flag";
import { StatusPill } from "@/components/emc/StatusPill";
import { ErrorState } from "@/components/emc/states";
import { useIsAdmin } from "@/pages/warmup/actions";
import { ParallelDialog } from "@/pages/warmup/ParallelDialog";
import { SWITCH, mailableText, statusWords } from "@/pages/warmup/simple";
import type { CountryRow } from "@/pages/warmup/simple";

interface Props {
  rows: CountryRow[];
  loading: boolean;
  error: string | null;
  onChanged: () => void;
}

export function CountryList({ rows, loading, error, onChanged }: Props) {
  const isAdmin = useIsAdmin();
  const [asking, setAsking] = useState<{ row: CountryRow; enabled: boolean } | null>(null);

  const columns: ColumnDef<CountryRow>[] = [
    {
      id: "country",
      header: "Ország",
      cell: ({ row }) => <Country code={row.original.country} className="font-medium" />,
    },
    {
      id: "account",
      header: "SES-fiók",
      cell: ({ row }) =>
        row.original.account ? (
          <span className="data-mono font-semibold" title={`pool: ${row.original.pool}`}>
            {row.original.account}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      id: "mailable",
      header: "Küldhető most",
      cell: ({ row }) => (
        <span className={row.original.mailable ? "tabular-nums" : "text-muted-foreground"}>
          {mailableText(row.original)}
        </span>
      ),
      meta: { wrap: true },
    },
    {
      id: "status",
      header: "Állapot",
      cell: ({ row }) => {
        const look = statusWords(row.original);
        return (
          <StatusPill tone={look.tone} title={look.title}>
            {look.word}
          </StatusPill>
        );
      },
    },
    {
      id: "parallel",
      header: "Klaviyo is küld",
      cell: ({ row }) => {
        const item = row.original;
        const id = `parallel-${item.country}`;
        return (
          <span className="flex items-center gap-2">
            <Switch
              id={id}
              aria-label={`${item.country}: a Klaviyo is küld`}
              checked={item.parallel}
              disabled={!isAdmin || !item.switchable || item.handover === null}
              title={
                item.switchable
                  ? undefined
                  : item.status === "cut_over"
                    ? "Átállva: a Klaviyo már nem küld, nincs mit kapcsolni."
                    : "Nincs átadás-sor: semmi nem korlátozza az országot."
              }
              onCheckedChange={(next) => setAsking({ row: item, enabled: next })}
            />
            <span className="text-label text-muted-foreground">{item.parallel ? "be" : "ki"}</span>
          </span>
        );
      },
    },
  ];

  return (
    <Card id="countries" className="scroll-mt-6">
      <CardHeader>
        <CardTitle>Országok</CardTitle>
        <CardDescription>
          Zárt országba innen senkinek nem megy levél, és a fiók számából sem jut rá. A {SWITCH}{" "}
          kapcsolóval nyitod meg: a Klaviyo tovább küld nekik, és mi is — minden más kizárás és a napi
          szám él. A fiók felosztása azonnal követi.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {error !== null && <ErrorState message={error} onRetry={onChanged} />}
        <DataTable
          label="Országok"
          variant="plain"
          className="-mx-5.5 border-t border-separator"
          columns={columns}
          data={rows}
          loading={loading}
          getRowId={(item) => item.country}
          empty={<p className="px-4 py-6 text-center text-muted-foreground">Nincs ország.</p>}
        />
      </CardContent>
      {asking !== null && asking.row.handover !== null && (
        <ParallelDialog
          row={asking.row.handover}
          enabled={asking.enabled}
          open
          onOpenChange={(next) => !next && setAsking(null)}
          onDone={(message) => {
            toast(message);
            setAsking(null);
            onChanged();
          }}
        />
      )}
    </Card>
  );
}
